"use client";

/**
 * 租户套餐定价卡片组件
 * 展示单个租户套餐的详细信息和价格
 */

import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Check,
  Users,
  Zap,
  Shield,
  HeadphonesIcon,
  Building2,
} from "lucide-react";
import {
  TenantPlanDetails,
  useCalculatePrice,
  formatPrice,
} from "@/hooks/useBillingPlans";

interface TenantPricingCardProps {
  plan: TenantPlanDetails;
  onSelect?: (
    plan: TenantPlanDetails,
    seats: number,
    billingCycle: "monthly" | "yearly",
  ) => void;
  highlighted?: boolean;
  hasSubscription?: boolean;
}

export default function TenantPricingCard({
  plan,
  onSelect,
  highlighted,
  hasSubscription = false,
}: TenantPricingCardProps) {
  const t = useTranslations("billingPage");
  const [seats, setSeats] = useState(plan.seats?.included || plan.baseSeats || 5);
  const [isYearly, setIsYearly] = useState(true); // 默认推荐年度套餐
  const { breakdown, calculate, loading } = useCalculatePrice();

  const billingCycle = isYearly ? "yearly" : "monthly";
  // 根据 billingCycle 选择正确的 planCode
  // 数据库中月度和年度套餐是分开存储的，需要使用对应的 planCode
  const basePlanCode = plan.code.replace("_YEARLY", "_MONTHLY");
  const planCode = isYearly
    ? basePlanCode.replace("_MONTHLY", "_YEARLY")
    : basePlanCode;

  // 调试日志 - 追踪 planCode 变化
  console.log("[TenantPricingCard] planCode calculation", {
    tier: plan.tier,
    originalPlanCode: plan.code,
    basePlanCode,
    isYearly,
    billingCycle,
    finalPlanCode: planCode,
  });

  // 计算价格
  useEffect(() => {
    console.log("[TenantPricingCard] Calling calculate API", {
      planCode,
      seats,
      billingCycle,
    });
    calculate(planCode, seats, billingCycle);
  }, [planCode, seats, billingCycle, calculate]);

  // 调试日志
  useEffect(() => {
    if (breakdown && plan.tier?.toLowerCase() === "business") {
      console.log("[TenantPricingCard Debug]", {
        tier: plan.tier,
        seats,
        billingCycle,
        planCode,
        breakdown,
        displayPrice: breakdown.total,
        formattedPrice: formatPrice(breakdown.total, breakdown.currency),
      });
    }
  }, [breakdown, seats, billingCycle, planCode, plan.tier]);

  const handleSelect = () => {
    // 传递根据 billingCycle 计算后的 plan 对象，确保年付/月付的 planCode 正确
    const planForSelect = {
      ...plan,
      code: planCode,
    };
    onSelect?.(planForSelect, seats, billingCycle);
  };

  // 获取功能列表
  const features = plan.metadata?.features || [];

  // 获取套餐层级颜色
  const getTierColor = () => {
    switch (plan.tier?.toLowerCase()) {
      case "team":
        return "bg-blue-500";
      case "business":
        return "bg-purple-500";
      case "education":
        return "bg-green-500";
      case "startup":
        return "bg-orange-500";
      case "enterprise":
        return "bg-gray-800";
      default:
        return "bg-gray-500";
    }
  };

  // 获取套餐图标
  const getTierIcon = () => {
    switch (plan.tier?.toLowerCase()) {
      case "team":
        return <Users className="h-5 w-5" />;
      case "business":
        return <Building2 className="h-5 w-5" />;
      case "education":
        return <Zap className="h-5 w-5" />;
      case "startup":
        return <Zap className="h-5 w-5" />;
      case "enterprise":
        return <Shield className="h-5 w-5" />;
      default:
        return <Users className="h-5 w-5" />;
    }
  };

  return (
    <Card
      className={`relative flex flex-col ${highlighted ? "border-primary shadow-lg scale-105" : ""}`}
    >
      {/* 推荐标签 */}
      {plan.featured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-gradient-to-r from-amber-500 to-orange-400 text-white border-0">
            {t("recommended")}
          </Badge>
        </div>
      )}

      {/* 资格要求标签 */}
      {plan.eligibility?.requiresVerification && (
        <div className="absolute -top-3 right-4">
          <Badge variant="outline" className="bg-background">
            {t("requiresVerification")}
          </Badge>
        </div>
      )}

      <CardHeader className="text-center pb-2">
        <div
          className={`mx-auto w-12 h-12 rounded-full ${getTierColor()} text-white flex items-center justify-center mb-2`}
        >
          {getTierIcon()}
        </div>
        <CardTitle>
          {plan.tier?.toLowerCase() === "team" && t("tierTeam")}
          {plan.tier?.toLowerCase() === "business" && t("tierBusiness")}
          {plan.tier?.toLowerCase() === "education" && t("tierEducation")}
          {plan.tier?.toLowerCase() === "startup" && t("tierStartup")}
          {plan.tier?.toLowerCase() === "enterprise" && t("tierEnterprise")}
        </CardTitle>
        <CardDescription>
          {plan.tier?.toLowerCase() === "team" && t("tierTeamDesc")}
          {plan.tier?.toLowerCase() === "business" && t("tierBusinessDesc")}
          {plan.tier?.toLowerCase() === "education" && t("tierEducationDesc")}
          {plan.tier?.toLowerCase() === "startup" && t("tierStartupDesc")}
          {plan.tier?.toLowerCase() === "enterprise" && t("tierEnterpriseDesc")}
        </CardDescription>
      </CardHeader>

      <CardContent className="flex-1 space-y-4">
        {/* 价格显示 */}
        <div className="text-center">
          {plan.tier?.toLowerCase() === "enterprise" ? (
            <div className="text-metric font-bold">{t("contactSalesBtn")}</div>
          ) : (
            <>
              <div className="text-metric font-bold" data-testid="price-display" data-breakdown-total={breakdown?.total}>
                {loading
                  ? "..."
                  : breakdown
                    ? formatPrice(breakdown.total, breakdown.currency)
                    : formatPrice(plan.basePrice || 0, plan.currency)}
              </div>
              <div className="text-subtle">
                {isYearly ? t("perYear") : t("perMonth")}
                {breakdown?.savings && breakdown.savings > 0 && (
                  <span className="text-green-600 ml-2">
                    {t("savingsAmount", {
                      amount: formatPrice(
                        breakdown.savings,
                        breakdown.currency,
                      ),
                    })}
                  </span>
                )}
              </div>
            </>
          )}
        </div>

        {/* 计费周期切换 */}
        {plan.tier?.toLowerCase() !== "enterprise" && (
          <div className="flex items-center justify-center gap-2">
            <Label
              htmlFor={`billing-${plan.code}`}
              className={!isYearly ? "font-semibold" : ""}
            >
              {t("monthly")}
            </Label>
            <Switch
              id={`billing-${plan.code}`}
              checked={isYearly}
              onCheckedChange={setIsYearly}
              variant="soft"
            />
            <Label
              htmlFor={`billing-${plan.code}`}
              className={isYearly ? "font-semibold" : ""}
            >
              {t("yearly")}
              <Badge
                variant="secondary"
                className="ml-1 text-small bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
              >
                {t("savePercent")}
              </Badge>
            </Label>
          </div>
        )}

        {/* 席位选择 */}
        {plan.tier?.toLowerCase() !== "enterprise" && (
          <div className="space-y-2">
            <div className="flex justify-between text-body">
              <span>{t("seatCount")}</span>
              <span className="font-semibold">
                {seats} {t("seats")}
              </span>
            </div>
            <Slider
              value={[seats]}
              onValueChange={([value]) => setSeats(value)}
              min={plan.seats.min}
              max={plan.seats.max}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-subtle">
              <span>{t("minSeats", { count: plan.seats.min })}</span>
              <span>{t("maxSeats", { count: plan.seats.max })}</span>
            </div>
            {seats > (plan.seats?.included || plan.baseSeats || 5) && breakdown && (
              <div className="text-subtle text-center">
                {t("includedSeats", {
                  included: breakdown.includedSeats,
                  extra: breakdown.extraSeats,
                  price: formatPrice(
                    breakdown.pricePerExtraSeat,
                    breakdown.currency,
                  ),
                })}
              </div>
            )}
          </div>
        )}

        {/* 功能列表 */}
        <div className="space-y-2 pt-4 border-t">
          <div className="text-body font-semibold">{t("includedFeatures")}</div>
          <ul className="space-y-1.5">
            {/* 基础功能 */}
            <li className="flex items-center gap-2 text-body">
              <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span>{t("seatsStart", { count: plan.seats.included })}</span>
            </li>
            <li className="flex items-center gap-2 text-body">
              <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span>
                {plan.quotaAllocation.mode === "SHARED"
                  ? t("quotaShared")
                  : t("quotaPerSeat")}
                {plan.quotaAllocation.perSeatQuota &&
                  plan.quotaAllocation.perSeatQuota > 0 && (
                    <span className="text-subtle">
                      {" "}
                      {t("perSeatQuota", {
                        quota: plan.quotaAllocation.perSeatQuota,
                      })}
                    </span>
                  )}
              </span>
            </li>
            <li className="flex items-center gap-2 text-body">
              <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span>
                {t("maxAdmins", {
                  count:
                    plan.management.maxAdmins === -1
                      ? t("unlimitedAdmins")
                      : plan.management.maxAdmins,
                })}
              </span>
            </li>

            {/* 动态功能 */}
            {plan.featuresConfig.api && (
              <li className="flex items-center gap-2 text-body">
                <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                <span>{t("apiAccess")}</span>
              </li>
            )}
            {plan.featuresConfig.sso && (
              <li className="flex items-center gap-2 text-body">
                <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                <span>{t("ssoLogin")}</span>
              </li>
            )}
            {plan.featuresConfig.advancedReports && (
              <li className="flex items-center gap-2 text-body">
                <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                <span>{t("advancedReports")}</span>
              </li>
            )}
            {plan.featuresConfig.whiteLabel && (
              <li className="flex items-center gap-2 text-body">
                <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                <span>{t("whiteLabel")}</span>
              </li>
            )}
            {plan.featuresConfig.customCourses && (
              <li className="flex items-center gap-2 text-body">
                <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                <span>{t("customCourses")}</span>
              </li>
            )}

            {/* 支持级别 */}
            <li className="flex items-center gap-2 text-body">
              <HeadphonesIcon className="h-4 w-4 text-blue-500 flex-shrink-0" />
              <span>
                {plan.support.level === "EMAIL" && t("emailSupport")}
                {plan.support.level === "PRIORITY" && t("prioritySupport")}
                {plan.support.level === "DEDICATED" && t("dedicatedSupport")}
                {plan.support.responseHours > 0 &&
                  ` ${t("responseHours", { hours: plan.support.responseHours })}`}
              </span>
            </li>
            {plan.support.hasCSM && (
              <li className="flex items-center gap-2 text-body">
                <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                <span>{t("dedicatedCSM")}</span>
              </li>
            )}
          </ul>

          {/* 试用期信息 */}
          {plan.trial.enabled && plan.trial.days > 0 && (
            <div className="mt-3 p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded text-subtle text-center text-emerald-700 dark:text-emerald-400">
              🎁 {plan.trial.days} {t("trialDays")}
            </div>
          )}

          {/* 资格折扣 */}
          {plan.eligibility?.discountPercent &&
            plan.eligibility.discountPercent > 0 && (
              <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded text-subtle text-center text-green-700 dark:text-green-300">
                {t("eligibilityDiscount", {
                  percent: plan.eligibility.discountPercent,
                })}
              </div>
            )}
        </div>
      </CardContent>

      <CardFooter>
        <Button
          className={`w-full ${highlighted ? "bg-gradient-to-r from-amber-500 to-orange-400 hover:from-amber-600 hover:to-orange-500 text-white border-0" : ""}`}
          variant={highlighted ? "default" : "outline"}
          onClick={handleSelect}
        >
          {plan.tier?.toLowerCase() === "enterprise" && t("contactSalesBtn")}
          {(plan.tier?.toLowerCase() === "education" ||
            plan.tier?.toLowerCase() === "startup") &&
            t("applyTrial")}
          {(plan.tier?.toLowerCase() === "team" ||
            plan.tier?.toLowerCase() === "business") &&
            (hasSubscription ? t("switchPlan") : t("startTrial"))}
        </Button>
      </CardFooter>
    </Card>
  );
}
